import type { InventoryItem, PackageDef, PricingConfig, SurfaceDef } from '@lbhl/shared';

export interface Tier {
  pkg: PackageDef;
  /**
   * The surfaces this package ADDS — the ones no lower tier already measures.
   *
   * This is what makes the form say "Front roofline" under Essential and only the
   * additional roofline under Signature, without any code knowing a tier by name.
   * It falls out of the package/surface join, which is the same thing that makes a
   * higher tier cost more.
   */
  introduces: SurfaceDef[];
}

/** Packages in config order, each with the surfaces it introduces. */
export function tiersOf(pricing: PricingConfig): Tier[] {
  const packages = [...pricing.packages].sort((a, b) => a.sort - b.sort);
  const seen = new Set<string>();

  return packages.map((pkg) => {
    const introduces = pkg.surfaces
      .filter((code) => !seen.has(code))
      .map((code) => pricing.surfaces.find((s) => s.code === code))
      .filter((s): s is SurfaceDef => s !== undefined)
      .sort((a, b) => a.sort - b.sort);

    for (const code of pkg.surfaces) seen.add(code);
    return { pkg, introduces };
  });
}

/**
 * The inventory items a bidder counts by hand, in inventory order.
 *
 * Read from the config's `manual_entry` rules rather than a hardcoded list, so an
 * admin adding a ninth consumable gets a field for it without a code change.
 */
export function engineeringItems(pricing: PricingConfig): InventoryItem[] {
  const manual = new Set(
    pricing.bomRules.filter((r) => r.driver === 'manual_entry').map((r) => r.sku),
  );
  return pricing.inventoryItems.filter((i) => manual.has(i.sku));
}

/** Rounds to the two decimals feet are stored with, and kills float drift. */
export function round2(n: number): number {
  return Number(n.toFixed(2));
}

/**
 * The usable numbers out of a segment group: blanks and zeros dropped, strands
 * forced to whole units.
 *
 * Rounding here rather than only on the sum is deliberate. The engine checks that
 * the segments add up to the priced figure, so the two have to be rounded the same
 * way or a decimal entry would fail its own validation.
 */
export function parseSegments(
  segments: ReadonlyArray<{ value: string }>,
  isAccent: boolean,
): number[] {
  return segments
    .map((s) => Number(s.value))
    .filter((n) => Number.isFinite(n) && n > 0)
    .map((n) => (isAccent ? Math.round(n) : round2(n)));
}

export function sumOf(values: number[]): number {
  return round2(values.reduce((a, b) => a + b, 0));
}
